// Registro de nomina que devuelve el buscador de SOEID
// (GET /<tenant>/api/banamex/nomina/search/, ver apis/apiSoeid.js).
//
// El backend ya parte el archivo del banco (21 columnas separadas por "|") y
// entrega el registro mapeado en camelCase. Aqui solo se traduce a las llaves
// snake_case con las que se guarda dentro del involucrado: son las que leen
// InvolvedDetails, employment.js y los registros ya validados, y renombrarlas
// dejaria esos expedientes sin datos.
//
// Puesto, oficina y seccion siguen llegando como CODIGO de catalogo
// ("00165224"), no como texto: para mostrarlos legibles falta el catalogo de
// cada uno. Mientras no exista, se guarda el codigo tal cual.

// Estatus conocidos; cualquier otro codigo se muestra tal cual
export const SOEID_STATUS_ACTIVE = "A";

const clean = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * Antiguedad a la fecha de referencia (por omision, hoy). Devuelve años y meses
 * para que la pantalla los formatee; null si no hay fecha de ingreso.
 *
 * Ojo: la antiguedad envejece sola, por eso lo que se guarda en el expediente
 * es la fecha de ingreso y este calculo se hace al momento de mostrarla.
 */
export const getSeniority = (hireDate, reference = new Date()) => {
  if (!hireDate) return null;

  const start = new Date(`${hireDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;

  const end = reference instanceof Date ? reference : new Date(reference);
  if (Number.isNaN(end.getTime()) || end < start) return null;

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (end.getDate() < start.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months };
};

/** Jefe directo, tal como lo resuelve el backend contra la misma nomina. */
const normalizeManager = (manager) =>
  manager
    ? {
        geid: manager.geid ?? null,
        soeid: manager.soeid ?? "",
        employee_number: manager.employeeNumber ?? "",
        full_name: manager.fullName ?? "",
        status: manager.status ?? "",
        company: manager.company ?? null,
      }
    : null;

/**
 * Registro del endpoint -> objeto que se guarda dentro del involucrado.
 * @param {Object} record un elemento de `results[].records`
 */
export const normalizeSoeidRecord = (record) => {
  const source = record ?? {};

  return {
    // Identificadores
    soeid: clean(source.soeid),
    geid: source.geid ?? null,
    employee_number: clean(source.employeeNumber), // nomina
    mocode: source.mocode ?? null,
    rits_id: source.ritsId ?? null,

    // Nombre
    first_names: clean(source.firstNames),
    last_name_1: clean(source.lastName1),
    last_name_2: clean(source.lastName2),
    full_name: clean(source.fullName),

    // Adscripcion (puesto, oficina y seccion son codigos de catalogo)
    position_code: source.position ?? null,
    office_code: source.office ?? null,
    section_code: source.section ?? null,
    company: source.company ?? null,
    manager_geid: source.managerGeid ?? null,
    // El backend resuelve el GEID del jefe contra la nomina; queda en null
    // cuando ese GEID no esta en el archivo (jefes de otra geografia).
    manager: normalizeManager(source.manager),

    // Situacion laboral. `active` viene calculado: contempla que un activo
    // arrastre una fecha de renuncia vieja por reingreso.
    status: clean(source.status),
    active: source.active ?? null,
    days_inactive: source.daysInactive ?? null,
    hire_date: source.hireDate ?? null,
    group_hire_date: source.groupHireDate ?? null,
    resignation_date: source.resignationDate ?? null,
    movement_date: source.movementDate ?? null,

    // Bloque de ejecutivo: solo trae datos si la persona atiende clientes
    executive_status: source.executiveStatus ?? null,
    executive_office_code: source.executiveOffice ?? null,
    executive_status_date: source.executiveStatusDate ?? null,
    executive_start_date: source.executiveStartDate ?? null,
    executive_updated_at: source.executiveLastUpdate ?? null,

    // Registro sin procesar, por si hace falta cotejar contra el origen
    raw: source,
  };
};

/**
 * ¿La persona sigue activa?
 *
 * Se prefiere `active` del backend, que ya contempla al activo con fecha de
 * renuncia vieja por reingreso. El calculo local queda de respaldo para los
 * registros validados antes de conectar el endpoint, que no traen la bandera.
 */
export const isEmployeeActive = (employee) => {
  if (typeof employee?.active === "boolean") return employee.active;
  return !employee?.resignation_date && employee?.status === SOEID_STATUS_ACTIVE;
};
